// src/components/AdminSection.js
//
// Admin mint console for the Prospect Worker NFT (UUPS proxy, impl = V7).
//
// The deployed contract has no batchMint(); the two admin mint paths are:
//   • airdrop(address[])              – appends above the highest issued ID.
//   • massMintWithIds(address[],uint256[]) – the only gap-filling path, and what
//                                       this panel uses.
//

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import { ethers } from 'ethers';
import axios from 'axios';
import { parseTxError } from '../utils/parseTxError';
import { getApiBaseUrl } from '../utils/apiConfig';
import V7Abi from '../contracts/Prospect2WorkerLicenseV7.json';

const GAS_LIMIT_BUFFER_PCT = 110; // +10 % safety

/**
 * Locally-thrown validation copy is already user-facing and must NOT be run
 * through parseTxError — that path only understands chain/RPC payloads and
 * collapses anything it does not recognise into a generic message.
 */
function validationError(message) {
  return Object.assign(new Error(message), { isValidation: true });
}

function messageForUser(err) {
  return err && err.isValidation ? err.message : parseTxError(err);
}

/** BigNumber -> number for a settled read; null when the read was unavailable. */
function readNumber(result) {
  if (!result || result.status !== 'fulfilled' || result.value === null || result.value === undefined) {
    return null;
  }
  try {
    return typeof result.value.toNumber === 'function'
      ? result.value.toNumber()
      : Number(result.value);
  } catch (err) {
    return null;
  }
}

/** Renders a supply field that the deployment does not expose. */
function fmt(value) {
  return value === null || value === undefined ? '—' : value;
}

async function getBaseFeeOverrides(provider) {
  const fee = await provider.getFeeData();
  return {
    maxFeePerGas:        fee.maxFeePerGas,
    maxPriorityFeePerGas: fee.maxPriorityFeePerGas ??
      (fee.maxFeePerGas ? fee.maxFeePerGas.div(10) : undefined)
  };
}

/**
 * Ids already present on-chain, per the nft-api indexer. Any id the indexer
 * knows about is unavailable — massMintWithIds requires !_exists(id).
 */
async function fetchTakenIds() {
  const { data } = await axios.get(`${getApiBaseUrl()}/nfts`);
  const taken = new Set();
  Object.keys(data || {}).forEach((key) => {
    const id = parseInt(key, 10);
    if (!Number.isNaN(id)) taken.add(id);
  });
  return taken;
}

/** Select the lowest unused IDs in the live collection range. */
function pickFreeIds(taken, count, maxTokenId) {
  const ids = [];
  for (let id = 1; id <= maxTokenId && ids.length < count; id++) {
    if (!taken.has(id)) ids.push(id);
  }
  return ids;
}

function countFreeIds(taken, maxTokenId) {
  let free = 0;
  for (let id = 1; id <= maxTokenId; id++) {
    if (!taken.has(id)) free++;
  }
  return free;
}

const AdminSection = ({ contract, account, onRequireLogin, onMintSuccess }) => {
  const [recipientAddress, setRecipientAddress] = useState('');
  const [numberOfTokens, setNumberOfTokens] = useState('');
  const [minting, setMinting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // { recipient, ids }
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [supply, setSupply] = useState(null);
  const [supplyError, setSupplyError] = useState(null);
  const [supplyLoading, setSupplyLoading] = useState(false);
  const handleMintRef = useRef(null);

  // Reuse whatever signer/provider App.js already wired into the contract prop,
  // but bind the real V7 ABI so the admin-only views and massMintWithIds exist.
  const adminContract = useMemo(() => {
    if (!contract || !contract.address) return null;
    return new ethers.Contract(
      contract.address,
      V7Abi,
      contract.signer || contract.provider
    );
  }, [contract]);

  // remainingSupply / highestTokenId / MAX_MINT_ID_BATCH only exist on V7. On an
  // older deployment those three reads revert, so they are settled individually
  // and the panel keeps working on whatever did resolve.
  const refreshSupply = useCallback(async () => {
    if (!adminContract) return;
    setSupplyLoading(true);
    setSupplyError(null);
    try {
      const results = await Promise.allSettled([
        adminContract.totalSupply(),
        adminContract.maxSupply(),
        adminContract.remainingSupply(),
        adminContract.highestTokenId(),
        adminContract.MAX_MINT_ID_BATCH()
      ]);
      const [totalSupply, maxSupply, remainingRead, highestId, maxBatch] =
        results.map(readNumber);

      if (totalSupply === null && maxSupply === null) {
        // Nothing readable at all: bad address, wrong network, dead RPC.
        const firstRejection = results.find((r) => r.status === 'rejected');
        console.error('Failed to read supply state:', firstRejection && firstRejection.reason);
        setSupply(null);
        setSupplyError(parseTxError(firstRejection && firstRejection.reason));
        return;
      }

      // remainingSupply() is V7-only; derive it whenever both inputs resolved.
      const remaining =
        remainingRead !== null
          ? remainingRead
          : (maxSupply !== null && totalSupply !== null
            ? Math.max(maxSupply - totalSupply, 0)
            : null);

      const missing = [];
      if (remainingRead === null && remaining === null) missing.push('remainingSupply');
      if (highestId === null) missing.push('highestTokenId');
      if (maxBatch === null) missing.push('MAX_MINT_ID_BATCH');

      setSupply({ totalSupply, maxSupply, remaining, highestId, maxBatch, missing });
    } catch (err) {
      console.error('Failed to read supply state:', err);
      setSupplyError(parseTxError(err));
    } finally {
      setSupplyLoading(false);
    }
  }, [adminContract]);

  useEffect(() => { refreshSupply(); }, [refreshSupply]);

  const handlePreview = async () => {
    setError(null);
    setSuccess(null);
    setPreview(null);
    setPreviewing(true);

    try {
      const resolvedAddress = recipientAddress.trim();
      const tokenCount = parseInt(numberOfTokens.trim(), 10);

      if (!ethers.utils.isAddress(resolvedAddress)) {
        throw validationError('Invalid Ethereum address.');
      }
      if (isNaN(tokenCount) || tokenCount <= 0) {
        throw validationError('Please enter a valid number of tokens.');
      }
      if (!supply || supply.maxSupply === null) {
        throw validationError('Supply state is not loaded yet. Refresh and try again.');
      }
      // Guards whose input is unavailable on this deployment are skipped;
      // estimateGas still surfaces the real on-chain revert at confirm time.
      if (supply.remaining !== null && tokenCount > supply.remaining) {
        throw validationError(
          `Only ${supply.remaining} of ${supply.maxSupply} remain. Raise maxSupply from the owner wallet.`
        );
      }
      if (supply.maxBatch !== null && tokenCount > supply.maxBatch) {
        throw validationError(
          `MAX_MINT_ID_BATCH is ${supply.maxBatch}. Mint at most ${supply.maxBatch} tokens per transaction.`
        );
      }

      const taken = await fetchTakenIds();
      if (taken.size === 0) {
        // Minting against an empty index would collide with every live token.
        throw validationError(
          'Indexer returned no minted tokens. Refusing to pick ids from stale data — retry shortly.'
        );
      }

      const freeCount = countFreeIds(taken, supply.maxSupply);
      if (freeCount === 0) {
        throw validationError(
          `No free ids remain in 1-${supply.maxSupply}.`
        );
      }
      if (tokenCount > freeCount) {
        throw validationError(
          `Only ${freeCount} free ids remain in 1-${supply.maxSupply}.`
        );
      }

      const ids = pickFreeIds(taken, tokenCount, supply.maxSupply);
      setPreview({ recipient: resolvedAddress, ids });
    } catch (err) {
      console.error('Mint preview failed:', err);
      setError(messageForUser(err));
    } finally {
      setPreviewing(false);
    }
  };

  const handleMint = async () => {
    setError(null);
    setSuccess(null);
    if (!preview) return;

    if (!account || !contract) {
      if (onRequireLogin) return onRequireLogin(async () => { await handleMintRef.current?.(); });
      return;
    }
    setMinting(true);

    try {
      const { recipient, ids } = preview;
      const recipients = ids.map(() => recipient);

      // ---------- (1) estimate gas ----------
      const rawGas = await adminContract.estimateGas.massMintWithIds(
        recipients,
        ids
      );
      const gasLimit = rawGas.mul(GAS_LIMIT_BUFFER_PCT).div(100);

      // ---------- (2) fee caps ----------
      const feeOverrides = await getBaseFeeOverrides(adminContract.provider);

      // ---------- (3) send tx ----------
      const tx = await adminContract.massMintWithIds(
        recipients,
        ids,
        {
          gasLimit,
          ...feeOverrides
        }
      );
      await tx.wait();

      onMintSuccess?.({ tokenIds: ids, owner: recipient });
      setSuccess(`Minted ${ids.length} token(s) to ${recipient}: ${ids.join(', ')}`);
      setPreview(null);
      setRecipientAddress('');
      setNumberOfTokens('');
    } catch (err) {
      console.error('Mass mint failed:', err);
      setError(messageForUser(err));
    } finally {
      setMinting(false);
      refreshSupply();
    }
  };

  handleMintRef.current = handleMint;

  const busy = minting || previewing;

  return (
    <div>
      <h3>Admin Mint</h3>

      <div style={{ marginBottom: '12px' }}>
        <strong>Supply</strong>{' '}
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={refreshSupply}
          disabled={supplyLoading || !adminContract}
        >
          {supplyLoading ? <Spinner as="span" animation="border" size="sm" /> : 'Refresh'}
        </Button>
        {supplyError && <p style={{ color: 'red' }}>{supplyError}</p>}
        {supply && (
          <>
            <ul style={{ marginTop: '8px', marginBottom: 0 }}>
              <li>Minted: {fmt(supply.totalSupply)} / {fmt(supply.maxSupply)}</li>
              <li>Remaining: {fmt(supply.remaining)}</li>
              <li>Highest token id: {fmt(supply.highestId)}</li>
              <li>Max ids per transaction: {fmt(supply.maxBatch)}</li>
            </ul>
            {supply.missing.length > 0 && (
              <p style={{ marginTop: '6px', marginBottom: 0, fontSize: '0.85em', color: '#666' }}>
                Not available on this deployment: {supply.missing.join(', ')}. Those pre-flight
                checks are skipped; the transaction still reverts on-chain if invalid.
              </p>
            )}
          </>
        )}
      </div>

      <Form>
        <Form.Group controlId="recipientAddress">
          <Form.Label>Enter Ethereum Address</Form.Label>
          <Form.Control
            type="text"
            placeholder="0x123..."
            value={recipientAddress}
            onChange={(e) => { setRecipientAddress(e.target.value); setPreview(null); }}
          />
        </Form.Group>
        <Form.Group controlId="numberOfTokens">
          <Form.Label>Number of Tokens</Form.Label>
          <Form.Control
            type="number"
            placeholder="Enter number of tokens to mint"
            value={numberOfTokens}
            onChange={(e) => { setNumberOfTokens(e.target.value); setPreview(null); }}
          />
          <Form.Text muted>
            Selects the lowest available IDs through {fmt(supply?.maxSupply)}.
          </Form.Text>
        </Form.Group>

        {error && <p style={{ color: 'red' }}>{error}</p>}
        {success && <Alert variant="success">{success}</Alert>}

        {preview && (
          <Alert variant="info">
            Will mint {preview.ids.length} token(s) to {preview.recipient}
            <br />
            Will mint ids: {preview.ids.join(', ')}
          </Alert>
        )}

        {!preview ? (
          <Button
            variant="primary"
            onClick={handlePreview}
            disabled={busy || !recipientAddress.trim() || !numberOfTokens.trim()}
          >
            {previewing ? <Spinner as="span" animation="border" size="sm" /> : 'Preview ids'}
          </Button>
        ) : (
          <>
            <Button
              variant="success"
              onClick={handleMint}
              disabled={busy}
            >
              {minting ? <Spinner as="span" animation="border" size="sm" /> : 'Confirm mint'}
            </Button>{' '}
            <Button
              variant="outline-secondary"
              onClick={() => setPreview(null)}
              disabled={busy}
            >
              Cancel
            </Button>
          </>
        )}
      </Form>
    </div>
  );
};

export default AdminSection;
